import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  ViewChild,
} from '@angular/core';
import { IMapMarker } from '@interfaces/common.interface';
import * as Leaflet from 'leaflet';
import { v4 as uuidv4 } from 'uuid';

const iconRetinaUrl = 'assets/marker-icon-2x.png';
const iconUrl = 'assets/marker-icon.png';
const shadowUrl = 'assets/marker-shadow.png';
const iconDefault = Leaflet.icon({
  iconRetinaUrl,
  iconUrl,
  shadowUrl,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  tooltipAnchor: [16, -28],
  shadowSize: [41, 41],
});
Leaflet.Marker.prototype.options.icon = iconDefault;

@Component({
  selector: 'app-map',
  templateUrl: './map.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MapComponent implements AfterViewInit {
  @ViewChild('mapContainer') mapContainer: ElementRef;
  @Input() zoom = 10;

  readonly mapId = `map-${uuidv4()}`;
  private centerRecord: Leaflet.LatLngExpression = [52.407465, 16.924019];
  private map: Leaflet.Map;
  private mapMarkers: IMapMarker[];
  private isMapInitialized = false;
  private areMarkersPlaced = false;

  constructor(private readonly changeDetector: ChangeDetectorRef) {}

  @Input() set markers(inputMarkers: IMapMarker[]) {
    if (inputMarkers && inputMarkers.length) {
      this.setMarkers(inputMarkers);
      if (this.isMapInitialized) {
        this.placeMarkers();
      }
    }
  }

  @Input() set center(centerCoords: Leaflet.LatLngExpression) {
    if (centerCoords) {
      this.centerRecord = centerCoords;
      if (this.isMapInitialized) {
        this.centerView();
      }
    }
  }

  ngAfterViewInit(): void {
    this.initMap();
  }

  invalidateSize(): void {
    this.map.invalidateSize();
    this.changeDetector.markForCheck();
  }

  private setMarkers(inputMarkers: IMapMarker[]): void {
    this.mapMarkers = inputMarkers.filter((marker) => {
      if (marker && marker.coordinates) {
        const isLongitudeSet = marker.coordinates.longitude || marker.coordinates.longitude === 0;
        const isLatitudeSet = marker.coordinates.latitude || marker.coordinates.latitude === 0;
        return isLongitudeSet && isLatitudeSet;
      }
      return false;
    });
  }

  private centerView(): void {
    this.map.panTo(this.centerRecord);
  }

  private initMap(): void {
    this.map = Leaflet.map(this.mapId, {
      center: this.centerRecord,
      zoom: 10,
    });
    const tiles = Leaflet.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '&copy; <a href="http://www.openstreetmap.org/copyright">OpenStreetMap</a>',
    });
    tiles.addTo(this.map);
    this.map.attributionControl.setPosition('bottomleft');
    this.map.setView(this.centerRecord, this.zoom);
    this.isMapInitialized = true;

    if (!this.areMarkersPlaced && this.mapMarkers) {
      this.placeMarkers();
    }
    this.invalidateSize();
  }

  private placeMarkers(): void {
    this.mapMarkers.forEach((markers) => {
      this.placeMarker(markers);
    });
  }

  private placeMarker(marker: IMapMarker): void {
    const pinnedMarker = Leaflet.marker([marker.coordinates.latitude, marker.coordinates.longitude]).addTo(this.map);
    if (marker.label) {
      pinnedMarker.bindTooltip(marker.label, {
        permanent: true,
        direction: 'right',
      });
    }
  }
}
